import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Play, Pause, Square, ChevronDown, ChevronUp, Volume2 } from "lucide-react";
import { assets, hero, links, identity } from "@/config";
import { useVoice } from "@/context/VoiceContext";
import { speak, playAudioFile, stopAllSpeech } from "@/lib/speech";
import { stagger, item } from "@/lib/motion";
import { Waveform } from "@/components/primitives";

const useOpeningStatement = () => {
  const [status, setStatus] = useState("idle"); // idle | playing | paused
  const [progress, setProgress] = useState(0);
  const ctrl = useRef(null);

  const stop = () => { ctrl.current?.stop(); ctrl.current = null; setStatus("idle"); setProgress(0); };
  const play = async () => {
    if (status === "paused") { ctrl.current?.resume(); setStatus("playing"); return; }
    if (status === "playing") return;
    stopAllSpeech();
    setProgress(0);
    const ok = await playAudioFile(hero.openingStatementAudio, { onProgress: setProgress, onEnd: () => { setStatus("idle"); setProgress(0); } });
    if (ok) { setStatus("playing"); return; }
    ctrl.current = speak(hero.transcript, {
      lang: "en-IN",
      onProgress: setProgress,
      onEnd: () => { setStatus("idle"); setProgress(0); ctrl.current = null; },
      onError: () => { setStatus("idle"); ctrl.current = null; },
    });
    setStatus("playing");
  };
  const pause = () => { ctrl.current?.pause(); setStatus("paused"); };
  return { status, progress, play, pause, stop };
};

/* Photo poster — the cutout exactly as uploaded, standing on the section edge with a red glow and soft blends. */
const PhotoPoster = () => (
  <div className="pointer-events-none relative h-[380px] w-full select-none sm:h-[460px] md:h-full" data-testid="hero-photo" role="img" aria-label={`${identity.name} — cutout portrait, pointing at the viewer`}>
    <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 75% 80% at 62% 78%, rgba(110,30,40,0.5), rgba(110,30,40,0.18) 45%, transparent 70%)" }} aria-hidden="true" />
    <img
      src={assets.heroPhoto}
      alt=""
      loading="eager"
      fetchPriority="high"
      className="absolute bottom-0 right-[4%] h-[96%] w-auto max-w-none object-contain object-bottom drop-shadow-[0_20px_60px_rgba(0,0,0,0.6)] lg:right-[10%]"
    />
    <div className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-noir via-noir/60 to-transparent" aria-hidden="true" />
    <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-noir to-transparent" aria-hidden="true" />
  </div>
);

export default function Hero() {
  const { registerPlayer, transcriptOpen, setTranscriptOpen } = useVoice();
  const player = useOpeningStatement();
  const reduce = useReducedMotion();

  useEffect(() => registerPlayer("opening", { play: player.play, stop: player.stop }), [registerPlayer, player.play, player.stop]);

  const playing = player.status === "playing";

  return (
    <section id="home" data-testid="section-home" className="relative overflow-hidden bg-noir">
      <div className="relative mx-auto grid min-h-[100svh] max-w-7xl grid-cols-1 px-5 pb-10 pt-28 sm:px-8 md:grid-cols-2 md:gap-6 md:pb-0">
        {/* LEFT — logo, then the statement */}
        <motion.div
          variants={reduce ? undefined : stagger(0.1)}
          initial={reduce ? false : "hidden"}
          animate="show"
          className="flex flex-col justify-center md:pr-6"
        >
          <motion.img variants={item} src={assets.logo} alt={identity.brand} className="h-24 w-auto self-start sm:h-28 lg:h-36" />

          <motion.h1 variants={item} className="mt-9 max-w-xl font-serif text-3xl leading-[1.12] text-cream sm:text-4xl lg:text-5xl">
            {hero.headline}
          </motion.h1>
          <motion.p variants={item} className="mt-4 max-w-xl text-base leading-relaxed text-gray2 sm:text-lg">
            {hero.subhead}
          </motion.p>

          <motion.div variants={item} className="mt-9">
            <div className="hairline mb-4 w-28" aria-hidden="true" />
            <p className="text-sm italic text-gray2">{hero.tagline}</p>
          </motion.div>

          <motion.div variants={item} className="mt-9 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-opening-statement"
              onClick={playing ? player.pause : player.play}
              aria-pressed={playing}
              className={`inline-flex items-center gap-3 rounded-md px-6 py-3.5 text-[15px] font-medium text-cream transition-[background-color,box-shadow] ${
                playing ? "bg-[#5A1820] shadow-[0_0_36px_rgba(110,30,40,0.55)]" : "bg-oxblood shadow-[0_0_22px_rgba(110,30,40,0.3)] hover:shadow-[0_0_36px_rgba(110,30,40,0.45)]"
              }`}
            >
              {playing ? <Waveform bars={4} active className="h-4 text-cream" /> : <Volume2 className="h-4 w-4" />}
              {hero.primaryCta}
            </button>
            <a
              data-testid="hero-linkedin"
              href={links.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-md border border-cream/25 px-6 py-3.5 text-[15px] font-medium text-cream transition-colors hover:border-cream/60 hover:bg-cream/5"
            >
              {hero.secondaryCta}
            </a>
          </motion.div>

          <motion.button
            variants={item}
            data-testid="hero-transcript-link"
            onClick={() => setTranscriptOpen(!transcriptOpen)}
            aria-expanded={transcriptOpen}
            className="mt-6 inline-flex items-center gap-1.5 self-start text-sm text-gray2 underline decoration-cream/30 underline-offset-4 transition-colors hover:text-cream"
          >
            {hero.transcriptLink} {transcriptOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </motion.button>

          <AnimatePresence initial={false}>
            {transcriptOpen && (
              <motion.div
                data-testid="transcript-panel"
                initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
                aria-live="polite"
              >
                <div className="mt-4 max-w-xl border-l-2 border-gold/40 py-1 pl-4">
                  <p className="text-[15px] leading-relaxed text-gray2">{hero.transcript}</p>
                  <div className="mt-4 flex items-center gap-3">
                    <button data-testid="transcript-play" onClick={playing ? player.pause : player.play} aria-label={playing ? "Pause" : "Play"} className="grid h-9 w-9 place-items-center rounded-full bg-oxblood text-cream">
                      {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </button>
                    <div className="h-1 flex-1 overflow-hidden rounded-full bg-cream/10">
                      <div className="h-full bg-gold transition-[width] duration-300" style={{ width: `${player.progress * 100}%` }} />
                    </div>
                    {player.status !== "idle" && (
                      <button data-testid="opening-stop" onClick={player.stop} aria-label="Stop" className="text-gray2 hover:text-cream"><Square className="h-3.5 w-3.5 fill-current" /></button>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* RIGHT — the poster, beside the statement from tablet up */}
        <div className="relative mt-6 md:mt-0 md:self-stretch">
          <PhotoPoster />
        </div>
      </div>
    </section>
  );
}
